/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useContext, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import Layout from '../../containers/Layout';
import Navigation from '../../components/Navigation';
import Breadcrumbs from '../../components/Breadcrumbs';
import FilledButton from '../../components/Buttons/FilledButton';
import OutlinedButton from '../../components/Buttons/OutlinedButton';
import RoundedInput from '../../components/Inputs/RoundedInput';
import TierService from '../../services/TierService';
import { UiContext } from '../../context/UiContext';
import classes from './styles.module.scss';

const emptyForm = {
  name: '',
  description: '',
  priceId: '',
  amount: '',
  currency: 'usd',
  seatLimit: '',
  storageLimitMb: '',
  displayOrder: '',
};

export default function SuperAdminTiersPage() {
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useContext(UiContext);
  const { data: tiers, isLoading } = useQuery('tiers-admin', TierService.listAdmin);

  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const sortedTiers = useMemo(() => tiers?.data || [], [tiers]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setSelectedId(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    const isFree = form.name.trim().toLowerCase() === 'free';
    const parsedAmount =
      form.amount === '' ? null : Number(form.amount);

    const payload = {
      ...form,
      priceId: isFree ? null : form.priceId,
      amount: isFree ? 0 : parsedAmount,
      currency: 'usd',
      seatLimit: form.seatLimit ? Number(form.seatLimit) : 0,
      storageLimitMb: form.storageLimitMb ? Number(form.storageLimitMb) : 0,
      displayOrder: form.displayOrder ? Number(form.displayOrder) : 0,
    };

    try {
      if (selectedId) {
        await TierService.update(selectedId, payload);
        notifySuccess('Tier updated');
      } else {
        await TierService.create(payload);
        notifySuccess('Tier created');
      }
      queryClient.invalidateQueries('tiers-admin');
      resetForm();
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Failed to save tier');
    }
  };

  const toggleActive = async (tier) => {
    try {
      await TierService.update(tier.id, { isActive: !tier.isActive });
      queryClient.invalidateQueries('tiers-admin');
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Failed to update tier');
    }
  };

  const handleEdit = (tier) => {
    setSelectedId(tier.id);
    setForm({
      name: tier.name || '',
      description: tier.description || '',
      priceId: tier.priceId || '',
      amount: tier.amount != null ? String(tier.amount) : '',
      currency: tier.currency || 'usd',
      seatLimit: tier.seatLimit != null ? String(tier.seatLimit) : '',
      storageLimitMb: tier.storageLimitMb != null ? String(tier.storageLimitMb) : '',
      displayOrder: tier.displayOrder != null ? String(tier.displayOrder) : '',
    });
  };

  return (
    <Layout hasUserBlock>
      <div className={classes.SuperAdminTiersPage}>
        <Navigation />
        <div className={classes.container}>
          <div className={classes.breadcrumbsContainer}>
            <Breadcrumbs
              breadcrumbsOptions={[
                { label: 'Super Admin', to: '/super-admin' },
                { label: 'Tiers', to: '/super-admin/tiers' },
              ]}
            />
            <p className={classes.subtitle}>Manage pricing tiers and limits</p>
          </div>
          <div className={classes.sectionCard}>
          <div className={classes.sectionHeader}>
            <div>
              <h3>{selectedId ? 'Edit Tier' : 'Create Tier'}</h3>
              <p>Define plan limits and Stripe price mapping.</p>
            </div>
          </div>

          <div className={classes.formGrid}>
            <RoundedInput
              id="name"
              name="name"
              label="Name"
              value={form.name}
              onChange={handleChange}
            />
            <RoundedInput
              id="description"
              name="description"
              label="Description"
              value={form.description}
              onChange={handleChange}
            />
            <RoundedInput
              id="priceId"
              name="priceId"
              label="Stripe price ID"
              value={form.priceId}
              onChange={handleChange}
              disabled={form.name.toLowerCase() === 'free'}
              placeholder={form.name.toLowerCase() === 'free' ? 'Not used for Free' : ''}
            />
            <RoundedInput
              id="amount"
              name="amount"
              type="number"
              min="0"
              label="Amount (cents)"
              value={form.amount}
              onChange={handleChange}
              disabled={form.name.toLowerCase() === 'free'}
            />
            <RoundedInput
              id="currency"
              name="currency"
              label="Currency"
              value={form.currency}
              disabled
            />
            <RoundedInput
              id="seatLimit"
              name="seatLimit"
              type="number"
              min="0"
              label="Seat limit"
              value={form.seatLimit}
              onChange={handleChange}
            />
            <RoundedInput
              id="storageLimitMb"
              name="storageLimitMb"
              type="number"
              min="0"
              label="Storage (MB)"
              value={form.storageLimitMb}
              onChange={handleChange}
            />
            <RoundedInput
              id="displayOrder"
              name="displayOrder"
              type="number"
              min="0"
              label="Display order"
              value={form.displayOrder}
              onChange={handleChange}
            />
          </div>

          <div className={classes.actions}>
            {selectedId && (
              <OutlinedButton
                className={classes.secondaryButton}
                onClick={resetForm}
                style={{ minWidth: '280px' }}
              >
                Cancel edit
              </OutlinedButton>
            )}
            <FilledButton
              type="button"
              onClick={handleSave}
              style={{ minWidth: '280px' }}
            >
              {selectedId ? 'Update Tier' : 'Create Tier'}
            </FilledButton>
          </div>
        </div>

          <div className={classes.sectionCard}>
          <div className={classes.sectionHeader}>
            <div>
              <h3>Tiers</h3>
              <p>Active plans shown in the pricing page.</p>
            </div>
          </div>

          {isLoading && <div className={classes.loading}>Loading tiers…</div>}
          <div className={classes.tableContainer}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Price ID</th>
                  <th>Amount</th>
                  <th>Seats</th>
                  <th>Storage (MB)</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedTiers.map((tier) => (
                  <tr key={tier.id}>
                    <td>{tier.name}</td>
                    <td>{tier.priceId || '—'}</td>
                    <td>{tier.amount != null ? `$${((tier.amount || 0) / 100).toFixed(0)}` : 'Free'}</td>
                    <td>{tier.seatLimit}</td>
                    <td>{tier.storageLimitMb}</td>
                    <td>{tier.isActive ? 'Active' : 'Inactive'}</td>
                    <td className={classes.actionsCell}>
                      <div className={classes.tableActions}>
                        <FilledButton
                          className={classes.tableActionButton}
                          onClick={() => handleEdit(tier)}
                        >
                          Edit
                        </FilledButton>
                        <OutlinedButton
                          className={classes.tableActionButton}
                          onClick={() => toggleActive(tier)}
                        >
                          {tier.isActive ? 'Deactivate' : 'Activate'}
                        </OutlinedButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
